import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CheckCircle2, DollarSign, CreditCard, FileCheck, Briefcase, ShieldCheck, Receipt } from 'lucide-react';
import { getQualifications, getFAQs } from '../services/api';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Qualifications = () => {
  const [qualifications, setQualifications] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [quals, faqsData] = await Promise.all([
          getQualifications(),
          getFAQs()
        ]);
        setQualifications(quals);
        setFaqs(faqsData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);
  const iconMap = {
    DollarSign: DollarSign,
    CreditCard: CreditCard,
    FileCheck: FileCheck,
    Briefcase: Briefcase,
    ShieldCheck: ShieldCheck,
    Receipt: Receipt
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <p className="text-xl text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO 
        title="Tenant Qualifications"
        description="Learn about our rental application process and tenant qualifications. Understand income requirements, credit checks, and application steps for Otter River Properties in Lynchburg, VA."
        keywords="tenant qualifications Lynchburg, rental application process, renter requirements Virginia, credit check rental, income requirements rental property"
      />
      {/* Header */}
      <section className="bg-otter-green dark:bg-otter-green-700 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Tenant Qualifications</h1>
          <p className="text-xl text-blue-50 max-w-2xl">
            Understanding our application process and requirements
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Welcome Prospective Tenants!</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                At Otter River Properties, we're committed to maintaining quality communities and matching great tenants with wonderful homes. Our application process is designed to be fair, transparent, and efficient.
              </p>
              <p className="text-gray-600">
                Below you'll find our tenant qualification criteria. Meeting these requirements helps ensure a smooth rental experience for everyone involved.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Requirements Grid */}
        <div className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Requirements</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualifications.map((qual) => {
              const IconComponent = iconMap[qual.icon];
              return (
                <Card key={qual.id} className="border-2 hover:border-otter-green transition-all duration-300 dark:bg-gray-900 dark:border-gray-800">
                  <CardHeader>
                    <div className="w-14 h-14 mb-3 rounded-full bg-otter-green-100 dark:bg-otter-green-800/30 flex items-center justify-center">
                      <IconComponent className="h-7 w-7 text-otter-green dark:text-otter-green-400" />
                    </div>
                    <CardTitle className="text-xl dark:text-white">{qual.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="text-gray-600 dark:text-gray-400"
                      dangerouslySetInnerHTML={{ __html: qual.description }}
                    />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.id} value={`item-${index + 1}`} className="bg-white dark:bg-gray-900 border dark:border-gray-800 rounded-lg px-6">
                <AccordionTrigger className="text-lg font-semibold hover:text-otter-green dark:hover:text-otter-green-300 dark:text-white">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 dark:text-gray-400">
                  <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Application Process */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-900 border-2 border-otter-green/30 dark:border-otter-green/50">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Application Process Steps</h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-otter-green text-white flex items-center justify-center font-bold">1</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Find Your Perfect Home</h3>
                    <p className="text-gray-600 dark:text-gray-400">Browse our available listings and schedule viewings for properties that interest you.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-otter-green text-white flex items-center justify-center font-bold">2</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Submit Application</h3>
                    <p className="text-gray-600 dark:text-gray-400">Complete the rental application and pay the non-refundable application fee.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-otter-green text-white flex items-center justify-center font-bold">3</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Background & Credit Check</h3>
                    <p className="text-gray-600 dark:text-gray-400">We'll verify your information, check your credit, and contact your references.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-otter-green text-white flex items-center justify-center font-bold">4</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Approval & Lease Signing</h3>
                    <p className="text-gray-600 dark:text-gray-400">Once approved, review and sign your lease agreement and pay move-in costs.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-otter-green text-white flex items-center justify-center font-bold">5</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Move In!</h3>
                    <p className="text-gray-600 dark:text-gray-400">Complete your move-in inspection, receive your keys, and welcome home!</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="max-w-4xl mx-auto text-center">
          <Card className="bg-otter-green border-0">
            <CardContent className="p-12">
              <CheckCircle2 className="h-16 w-16 text-white mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Apply?</h2>
              <p className="text-blue-50 text-lg mb-6 max-w-2xl mx-auto">
                Browse our available properties and start your application today. We're here to help you find your perfect home!
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button asChild size="lg" className="bg-white text-otter-green hover:bg-gray-100 text-lg px-8">
                  <Link to="/listings">View Available Properties</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                  <Link to="/">Contact Us</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Qualifications;